'use client';

import { Button } from '@/components/ui/button';
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useAuth } from '@/contexts/auth-context';
import { useLanguage } from '@/contexts/language-context';
import { zodResolver } from '@hookform/resolvers/zod';
import { Eye, EyeOff, Loader2, X } from 'lucide-react';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import * as z from 'zod';
import { Field, FieldError, FieldGroup, FieldLabel } from './ui/field';
import { Input } from './ui/input';

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (command: 'event', eventName: CoachAuthEvent, parameters: CoachAuthEventParameters) => void;
  }
}

const GTM_ID = 'GTM-TF74G22R';
const GA_MEASUREMENT_ID = 'G-J7PTVM0GNH';
const COACH_LOGIN_SOURCE = 'aicoach_login';
const COACH_LOGIN_CHANNEL = 'aicoach';

type CoachAuthEvent = 'coach_login_click' | 'coach_sign_up_click';

interface CoachAuthEventParameters {
  send_to: typeof GA_MEASUREMENT_ID;
  source: typeof COACH_LOGIN_SOURCE;
  login_channel: typeof COACH_LOGIN_CHANNEL;
}

function sendCoachAuthEvent(event: CoachAuthEvent) {
  if (typeof window === 'undefined') return;

  window.dataLayer = window.dataLayer || [];
  window.gtag =
    window.gtag ||
    function gtag() {
      // Google tag requires its canonical array-like command format.
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer?.push(arguments);
    };

  window.gtag('event', event, {
    send_to: GA_MEASUREMENT_ID,
    source: COACH_LOGIN_SOURCE,
    login_channel: COACH_LOGIN_CHANNEL,
  });
}

interface LoginDialogContextType {
  openLoginDialog: () => void;
  closeLoginDialog: () => void;
}

interface LoginDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const LoginDialogContext = createContext<LoginDialogContextType | null>(null);

function loadLoginGtm() {
  if (typeof window === 'undefined') return;
  if (!GTM_ID) return;
  window.dataLayer = window.dataLayer || [];

  const existing = document.querySelector(`script[src^="https://www.googletagmanager.com/gtm.js?id=${GTM_ID}"]`);
  if (existing) return;

  window.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });

  const firstScript = document.getElementsByTagName('script')[0];
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`;
  firstScript?.parentNode?.insertBefore(script, firstScript);
}

export function LoginDialogProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);

  const openLoginDialog = useCallback(() => {
    loadLoginGtm();
    setOpen(true);
  }, []);

  const closeLoginDialog = useCallback(() => {
    setOpen(false);
  }, []);

  const value = useMemo(() => ({ openLoginDialog, closeLoginDialog }), [openLoginDialog, closeLoginDialog]);

  return (
    <LoginDialogContext.Provider value={value}>
      {children}
      <LoginDialog
        open={open}
        onOpenChange={setOpen}
      />
    </LoginDialogContext.Provider>
  );
}

export function useLoginDialog() {
  const context = useContext(LoginDialogContext);

  if (!context) {
    throw new Error('useLoginDialog must be used within LoginDialogProvider');
  }

  return context;
}

export function LoginButton({ className }: { className?: string }) {
  const { openLoginDialog } = useLoginDialog();
  const { t } = useLanguage();

  return (
    <Button
      variant="outline"
      onClick={openLoginDialog}
      className={className ?? 'bg-[#F98715] text-white/80 border-0 hover:bg-[#F98715] hover:text-white'}>
      {t('header.login')}
    </Button>
  );
}

const formSchema = z.object({
  username: z.string().min(1, 'Email is required'),
  password: z
    .string()
    .min(8, 'Password must be at least 8 characters')
    .max(31, 'Password must be less than 32 characters')
    .regex(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).+$/, 'Password must include uppercase, lowercase, a number, and a special character'),
});

export function LoginDialog({ open, onOpenChange }: LoginDialogProps) {
  const { setUser, setIsLogin } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const router = useRouter();

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      username: '',
      password: '',
    },
  });

  const isLoading = form.formState.isSubmitting;

  async function postLogin(data: z.infer<typeof formSchema>) {
    try {
      // Browser → WP directly so Set-Cookie (WP session + aifo_auth_token) lands
      // on the WordPress host / parent domain. Do not proxy via Coach BFF.
      const phpBase = (process.env.NEXT_PUBLIC_PHP_API_URL || '').replace(/\/$/, '');
      if (!phpBase) {
        toast.error('Login is not configured (NEXT_PUBLIC_PHP_API_URL).', { position: 'top-center' });
        return;
      }

      const response = await fetch(`${phpBase}/wp-json/aifo-jwt/v1/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          ...data,
          remember: true,
        }),
      });

      const loginInfo = await response.json().catch(() => null);

      if (!response.ok || !loginInfo?.success || !loginInfo?.token) {
        toast.error('Login failed. Please check your email or password.', { position: 'top-center' });
        return;
      }

      // Mirror JWT onto Coach origin for same-origin BFF (localhost:3000 ↔ WP IP).
      await fetch('/api/auth/set-token', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: loginInfo.token }),
      }).catch(() => null);

      const currentUserResponse = await fetch('/api/auth/user').catch(() => null);
      const currentUserData = currentUserResponse?.ok ? await currentUserResponse.json().catch(() => null) : null;
      const currentUser = currentUserData?.user;
      if (!currentUser) {
        setUser(null);
        setIsLogin(false);
        toast.error('Login succeeded, but the session could not be established. Please try again.', { position: 'top-center' });
        return;
      }

      setUser({
        ...currentUser,
        userId: currentUser.id,
      });
      setIsLogin(true);
      router.push('/');
      toast.success('Login successful.', { position: 'top-center' });
      onOpenChange(false);
      form.reset();
    } catch (error) {
      console.error('[Login] Error:', error);
      toast.error('Login failed. Please try again.', { position: 'top-center' });
    }
  }

  async function onSubmit(data: z.infer<typeof formSchema>) {
    await postLogin(data);
  }

  const handleLoginClick = useCallback(() => {
    sendCoachAuthEvent('coach_login_click');
  }, []);

  const handleSignUpClick = useCallback(() => {
    sendCoachAuthEvent('coach_sign_up_click');
    window.open(process.env.NEXT_PUBLIC_SIGN_UP_LINK || '', '_blank');
  }, []);

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (isLoading) return;
        onOpenChange(nextOpen);
      }}>
      <DialogContent
        showCloseButton={false}
        overlayClassName="z-[120]"
        className="z-[120] w-[min(92vw,402px)] max-h-[90vh] overflow-y-auto bg-transparent bg-[url('/login-modal-bg.svg')] bg-cover bg-center bg-no-repeat px-8 py-6 border-none">
        <DialogClose asChild>
          <button
            type="button"
            disabled={isLoading}
            aria-label="Close"
            className="absolute cursor-pointer right-4 top-4 text-white/80 transition-colors hover:text-white disabled:opacity-50 disabled:pointer-events-none">
            <X className="h-5 w-5" />
          </button>
        </DialogClose>
        <DialogHeader className="sm:text-center gap-1">
          <DialogTitle className="text-3xl font-bold text-white text-center">
            <div className="flex justify-center mb-3">
              <Image
                src="/login-logo.svg"
                alt="AIFO-Coach"
                width={88}
                height={40}
              />
            </div>
            <p>Log in to AIFO</p>
          </DialogTitle>
          <DialogDescription className="text-[#ccc] text-[16px]">Please enter your details below.</DialogDescription>
        </DialogHeader>
        <form
          id="login-form"
          onSubmit={form.handleSubmit(onSubmit)}>
          <FieldGroup className="mt-4 gap-4">
            <Controller
              name="username"
              control={form.control}
              render={({ field, fieldState }) => (
                <Field data-invalid={fieldState.invalid}>
                  <FieldLabel
                    htmlFor="email"
                    className="text-white">
                    Email address
                  </FieldLabel>
                  <Input
                    {...field}
                    className="text-white bg-[#231F20] border-0 placeholder:text-[#666]"
                    id="username"
                    aria-invalid={fieldState.invalid}
                    placeholder="Enter your email"
                    autoComplete="off"
                    disabled={isLoading}
                  />
                  {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
                </Field>
              )}
            />
            <Controller
              name="password"
              control={form.control}
              render={({ field, fieldState }) => (
                <Field data-invalid={fieldState.invalid}>
                  <FieldLabel
                    htmlFor="password"
                    className="text-white">
                    Password
                  </FieldLabel>
                  <div className="relative">
                    <Input
                      {...field}
                      type={showPassword ? 'text' : 'password'}
                      className="text-white bg-[#231F20] border-0 placeholder:text-[#666] pr-10"
                      id="password"
                      aria-invalid={fieldState.invalid}
                      placeholder="Enter your password"
                      autoComplete="off"
                      disabled={isLoading}
                    />
                    <button
                      type="button"
                      disabled={isLoading}
                      onClick={() => setShowPassword((prev) => !prev)}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-white/70 transition-colors hover:text-white disabled:opacity-50 disabled:pointer-events-none">
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
                </Field>
              )}
            />
          </FieldGroup>
        </form>
        <div className="mt-5 flex flex-col gap-3">
          <Button
            type="submit"
            className="h-12 bg-[#F98715] text-white hover:text-white hover:bg-[#F98715]"
            form="login-form"
            onClick={handleLoginClick}
            disabled={isLoading}>
            {isLoading && <Loader2 className="w-4 h-4 animate-spin" />}
            {isLoading ? 'Logging in...' : 'Log in'}
          </Button>
          <Button
            type="button"
            className="hover:bg-transparent h-12 bg-transparent border text-white border-white/16"
            disabled={isLoading}
            onClick={handleSignUpClick}>
            Sign UP
          </Button>
        </div>
        <div className="mt-3 text-center text-sm text-[#666]">
          <span>Don&apos;t have an account? </span>
          <button
            type="button"
            onClick={handleSignUpClick}
            className="underline underline-offset-4 cursor-pointer text-[#F98715] hover:text-[#F98715]">
            Sign UP now
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
